import { useEffect, useState, useRef, useMemo } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import QAModal from '../components/QAModal';
import { getUserRole } from '../components/Auth';
import { useLoading } from '../components/LoadingContext';
import '../styles/Curriculum.css';

const Curriculum = () => {
  const { loading, startLoading, finishLoading } = useLoading();
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [progress, setProgress] = useState('');
  const [fileList, setFileList] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const intervalRef = useRef(null);
  const userRole = getUserRole();
  const loginCode = sessionStorage.getItem('loginCode');

  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]);
  const [qaLoading, setQaLoading] = useState(false);
  const [showQA, setShowQA] = useState(false);

  const [completedCurriculums, setCompletedCurriculums] = useState([]);
  const [selectedFileToUpload, setSelectedFileToUpload] = useState(null);

  const API_URL = 'https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/handler';

  const parseCurriculumAndSession = (key) => {
    const match = key.match(/^(カリキュラム\d+)\/第(\d+)回\//);
    if (!match) return null;
    return {
      curriculumName: match[1],
      sessionNumber: parseInt(match[2], 10),
    };
  };

  const groupedFiles = useMemo(() => {
    const groups = {};
    fileList.forEach(file => {
      const parsed = parseCurriculumAndSession(file.key);
      if (!parsed) return;
      const { curriculumName, sessionNumber } = parsed;
      if (!groups[curriculumName]) groups[curriculumName] = [];
      if (!groups[curriculumName].includes(sessionNumber)) {
        groups[curriculumName].push(sessionNumber);
      }
    });
    return groups;
  }, [fileList]);

  const filteredFiles = useMemo(() => {
    return fileList.filter(file => {
      const parsed = parseCurriculumAndSession(file.key);
      return (
        parsed &&
        parsed.curriculumName === selectedCurriculum &&
        parsed.sessionNumber === selectedSession
      );
    });
  }, [fileList, selectedCurriculum, selectedSession]);

  const fetchProgress = async () => {
    startLoading();
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getProgress', loginCode }),
      });
      const data = await res.json();

      if (data.completedCurriculums) {
        setCompletedCurriculums(data.completedCurriculums);
      }

      if (data.progress === '管理者用') {
        setProgress(userRole === 9 ? '管理者用' : 'スタッフ用');
      } else if (data.progress) {
        setProgress(`${data.progress.curriculum_name} - 第${data.progress.session_number}回`);
      } else {
        setProgress('進行度なし');
      }
    } catch (error) {
      console.error('fetchProgress Error:', error);
      setProgress('進行度取得失敗');
    } finally {
      finishLoading();
    }
  };

  const fetchFiles = async () => {
    startLoading();
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listFiles', loginCode }),
      });
      const data = await res.json();
      setFileList(data.files);
    } catch (error) {
      console.error('fetchFiles Error:', error);
      setFileList([]);
    } finally {
      finishLoading();
    }
  };

  useEffect(() => {
    fetchProgress();
  }, [loginCode, userRole]);

  useEffect(() => {
    fetchFiles();
    intervalRef.current = setInterval(() => {
      console.log('🔄 署名付きURLを更新します');
      fetchFiles();
    }, 59 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    if (filteredFiles.length > 0) {
      const file = filteredFiles[0];
      setSelectedKey(file.key);
      setSelectedFileUrl(file.url);
      setSelectedVideoUrl(file.videoUrl || null);
    } else {
      setSelectedKey(null);
      setSelectedFileUrl(null);
      setSelectedVideoUrl(null);
    }
  }, [filteredFiles]);

  const handleAsk = async () => {
    if (!question.trim()) return;
    setQaLoading(true);
    startLoading();
    try {
      const res = await fetch(
        'https://uf6ufmqg3m.execute-api.ap-northeast-3.amazonaws.com/prod/FAQ',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question }),
        }
      );
      const data = await res.json();
      const entry = {
        question,
        answer: data.answer || '回答が取得できませんでした。',
        sources: data.sources || [],
        keywords: data.keywords || [],
      };
      setHistory([entry, ...history.slice(0, 2)]);
      setQuestion('');
    } catch (err) {
      alert('通信エラーが発生しました');
    } finally {
      setQaLoading(false);
      finishLoading();
    }
  };

  const getEmbedUrl = (youtubeUrl) => {
    const match = youtubeUrl.match(/v=([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const handleUpload = async () => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    if (!selectedFileToUpload || !selectedCurriculum || !selectedSession) {
      alert('提出対象が選択されていません');
      return;
    }

    if (selectedFileToUpload.size > MAX_FILE_SIZE) {
      alert('ファイルサイズが5MBを超えています。');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFileToUpload);
    formData.append('loginCode', loginCode);
    formData.append('curriculumName', selectedCurriculum);
    formData.append('sessionNumber', selectedSession.toString());
    formData.append('action', 'uploadDeliverable');

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        body: formData  // Content-Type自動付与
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || 'アップロード失敗');
      }

      alert('アップロード成功');
      fetchFiles();
    } catch (err) {
      console.error('アップロード失敗', err);
      alert('アップロードに失敗しました');
    }
  };

  return (
    <div className="page">
      <h1 className="title">カリキュラム受講ページ</h1>

      <Card title="現在の進行度">
        <p>{progress || '読み込み中...'}</p>
      </Card>

      <Card title="カリキュラム選択">
        <div className="curriculum-select-container">
          <label className="curriculum-select-label">カリキュラム名：</label>
          <select
            className="curriculum-select"
            value={selectedCurriculum}
            onChange={e => {
              setSelectedCurriculum(e.target.value);
              setSelectedSession(null);
            }}
          >
            <option value="">カリキュラムを選択</option>
            {Object.keys(groupedFiles).map(name => {
              const current = progress.match(/(カリキュラム\d+)/)?.[1];
              const isApplied = /^カリキュラム[4-9]/.test(name);
              const isCurrent = name === current;
              const isCompleted = completedCurriculums.includes(name);
              const isBlocked =
                isApplied &&
                !isCompleted &&
                !isCurrent &&
                current &&
                /^カリキュラム[4-9]/.test(current);

              return (
                <option key={name} value={name} disabled={isBlocked}>
                  {name}{isBlocked ? '（進行中の他カリキュラムが未完了）' : ''}
                </option>
              );
            })}
          </select>
        </div>

        {selectedCurriculum && (
          <div className="curriculum-select-container">
            <label className="curriculum-select-label">進行度を選択：</label>
            <select
              className="curriculum-select"
              value={selectedSession || ''}
              onChange={e => setSelectedSession(Number(e.target.value))}
            >
              <option value="">進行度を選択</option>
              {groupedFiles[selectedCurriculum].map(session => (
                <option key={session} value={session}>第{session}回</option>
              ))}
            </select>
          </div>
        )}
      </Card>

      {selectedFileUrl && selectedFileUrl !== '' && selectedFileUrl !== 'about:blank' && (
        <div>
          <Card title="教材プレビュー">
            <iframe
              src={selectedFileUrl}
              width="100%"
              height="700"
              style={{ border: '1px solid #ccc', borderRadius: '6px' }}
              title="教材PDFプレビュー"
            ></iframe>
          </Card>

          {userRole === 1 && selectedFileUrl && selectedFileUrl !== '' && selectedFileUrl !== 'about:blank' && (
            <Card title="成果物の提出">
              <p style={{ marginBottom: '8px', color: '#555' }}>※提出可能なファイル形式は <strong>ZIP</strong> のみ、<strong>5MB</strong>までです。</p>
              <input
                type="file"
                accept=".zip"
                onChange={(e) => setSelectedFileToUpload(e.target.files[0])}
              />
              <Button onClick={handleUpload} disabled={!selectedFileToUpload || !progress.includes('カリキュラム')}>
                ZIPファイルを提出する
              </Button>
            </Card>
          )}
        </div>
      )}

      {selectedVideoUrl && (
        <Card title="動画プレビュー">
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src={getEmbedUrl(selectedVideoUrl)}
              title="教材動画"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '6px'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </Card>
      )}

      <div className="qa-float-button">
        <Button onClick={() => setShowQA(true)}>📘 FAQを開く</Button>
      </div>

      <QAModal
        show={showQA}
        onClose={() => setShowQA(false)}
        question={question}
        setQuestion={setQuestion}
        handleAsk={handleAsk}
        loading={loading}
        history={history}
      />

      {loading && (
        <div className="overlay-spinner">
          <div className="spinner-icon" />
          <p>Loading...</p>
        </div>
      )}
    </div>
  );
};

export default Curriculum;
